import { useState } from 'react';
import { toast } from 'sonner';
import { operationsClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import type { Receipt } from './types';

/**
 * Saves a receipt's PDF through the staff route (`GET /receipts/:id.pdf`,
 * signed in and tenant-scoped), which serves every receipt in its current
 * state: paid, processing, returned or cancelled. Not the public link: that
 * one is the customer's, and it refuses a void receipt with 410.
 */
export async function downloadReceiptPdf(receipt: Pick<Receipt, 'id' | 'number'>): Promise<void> {
  const response = await operationsClient.get<Blob>(endpoints.receipts.pdf(receipt.id), {
    responseType: 'blob',
  });
  const url = URL.createObjectURL(response.data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${receipt.number}.pdf`;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** `downloadReceiptPdf` with a pending flag and an error toast, for buttons. */
export function useDownloadReceiptPdf() {
  const [isDownloading, setIsDownloading] = useState(false);

  async function download(receipt: Pick<Receipt, 'id' | 'number'>) {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadReceiptPdf(receipt);
    } catch {
      toast.error('Failed to download receipt PDF');
    } finally {
      setIsDownloading(false);
    }
  }

  return { download, isDownloading };
}
